// Geolocation wrapper — single place the app reads device position.
//
// Supports a dev/test override: set `localStorage["riverghost:fakeGps"]`
// to "lat,lng" and every consumer (chapters, caches map, radar, sharing)
// sees that position instead of the real GPS. Left enabled in production
// on purpose — it's the demo/testing tool for a party game.

export interface GeoPoint {
  lat: number;
  lng: number;
  accuracy?: number;
}

const FAKE_KEY = "riverghost:fakeGps";

export function fakePosition(): GeoPoint | null {
  try {
    const raw = localStorage.getItem(FAKE_KEY);
    if (!raw) return null;
    const [lat, lng] = raw.split(",").map((s) => Number(s.trim()));
    return Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng, accuracy: 5 }
      : null;
  } catch {
    return null;
  }
}

/** One-shot position; resolves null when unavailable or denied. */
export function getPosition(): Promise<GeoPoint | null> {
  const fake = fakePosition();
  if (fake) return Promise.resolve(fake);
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
    );
  });
}

/**
 * Continuous position updates. Returns a stop function.
 * onError fires when the device refuses (denied) or can't produce a fix.
 */
export function watchPosition(
  cb: (p: GeoPoint) => void,
  onError?: (reason: "denied" | "unavailable") => void,
): () => void {
  const fake = fakePosition();
  if (fake) {
    cb(fake);
    const t = setInterval(() => {
      const f = fakePosition();
      if (f) cb(f);
    }, 5000);
    return () => clearInterval(t);
  }
  if (!("geolocation" in navigator)) {
    onError?.("unavailable");
    return () => {};
  }
  const id = navigator.geolocation.watchPosition(
    (p) => cb({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    (err) => onError?.(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable"),
    { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
  );
  return () => navigator.geolocation.clearWatch(id);
}
